/**
 * Все документы клиента разом — в разделе «Должник» (решение 28.09.2026).
 *
 * Сотрудник загружает папку клиента целиком. Вид каждого документа определяет
 * сервер (/import/any), и сведения раскладываются по разделам: кредиторы,
 * имущество, доходы, перечень приложений. Ничего не вносится само: на одном
 * экране показано всё найденное, подходящее уже отмечено, и одна кнопка
 * вносит отмеченное во все разделы сразу.
 *
 * Разделы показаны теми же загрузчиками, что и в самих разделах (встроенный
 * режим, import-handle.ts), — поэтому правила одни: повтор долга приходит
 * без отметки, чужой ИНН виден, объект из двух выписок отмечен один раз.
 * Доходы — ответами по группам (income-suggestions.ts).
 */

import { useEffect, useRef, useState } from 'react';
import { buildAppendix, computeBranching, type AppendixItemEntry } from '@bd/domain';
import { api, type AppendixImport, type CreditorsImport, type IncomeImport, type PropertyImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';
import { CreditorDocumentsImport } from './CreditorDocumentsImport.js';
import { PropertyDocumentsImport } from './PropertyDocumentsImport.js';
import { AppendixDocumentsImport } from './AppendixDocumentsImport.js';
import { GROUP_TITLES, incomeSuggestions, mergeIncomePatches, type IncomeGroup, type IncomeSuggestion } from './income-suggestions.js';
import { existingKeys, withCreditors, withObligatory, withProperty } from './merge-imports.js';
import type { ImportHandle, Preloaded } from './import-handle.js';
import type { BlockProps } from './types.js';

interface Parsed {
  creditors: Preloaded<CreditorsImport>;
  property: Preloaded<PropertyImport>;
  income: IncomeImport[];
  appendix: Preloaded<AppendixImport>;
  /** Файлы, которые не распознаны или не читаются */
  failed: string[];
  total: number;
}

const GROUP_ORDER: IncomeGroup[] = ['employment', 'salary', 'entrepreneur', 'social'];

export function AllDocumentsImport({ profile, update, readOnly }: Pick<BlockProps, 'profile' | 'update' | 'readOnly'>) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [suggestions, setSuggestions] = useState<IncomeSuggestion[]>([]);
  const [done, setDone] = useState<string[]>([]);
  const [appendixItems, setAppendixItems] = useState<AppendixItemEntry[]>([]);

  const creditorsRef = useRef<ImportHandle>(null);
  const propertyRef = useRef<ImportHandle>(null);
  const appendixRef = useRef<ImportHandle>(null);

  useEffect(() => {
    void api
      .dictionary('appendix-items')
      .then((r) => setAppendixItems(r.entries as unknown as AppendixItemEntry[]))
      .catch(() => undefined);
  }, []);

  const debtorName = [profile.debtor.last, profile.debtor.first, profile.debtor.middle].filter(Boolean).join(' ');

  const load = async (files: File[]) => {
    setBusy(true);
    setParsed(null);
    setDone([]);
    const next: Parsed = {
      creditors: { results: [], errors: [] },
      property: { results: [], errors: [] },
      income: [],
      appendix: { results: [], errors: [] },
      failed: [],
      total: files.length,
    };
    const existing = existingKeys(profile);

    // По одному: отчёт бюро на сотни листов сервер разбирает секунды
    for (const [i, file] of files.entries()) {
      setProgress(`Читаю ${i + 1} из ${files.length}: ${file.name}`);
      try {
        const result = await api.importAnyDocument(profile.caseId, file, existing, debtorName);
        next.appendix.results.push(result.appendix);
        const blockError = result.blockError ? [`${file.name}: ${result.blockError}`] : [];
        if (result.block === 'creditors') {
          if (result.creditors) next.creditors.results.push(result.creditors);
          next.creditors.errors.push(...blockError);
        } else if (result.block === 'property') {
          if (result.property) next.property.results.push(result.property);
          next.property.errors.push(...blockError);
        } else if (result.block === 'income') {
          if (result.income) next.income.push(result.income);
          else next.failed.push(...blockError);
        }
      } catch (e) {
        next.failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }

    setSuggestions(incomeSuggestions(next.income, profile.debtor.inn));
    setParsed(next);
    setProgress('');
    setBusy(false);
  };

  /** В группе отмечено не больше одного ответа: выбор одного снимает остальные. */
  const toggle = (id: string) =>
    setSuggestions((current) => {
      const target = current.find((s) => s.id === id)!;
      return current.map((s) =>
        s.id === id ? { ...s, checked: !s.checked } : s.group === target.group && !target.checked ? { ...s, checked: false } : s,
      );
    });

  const acceptAll = () => {
    const messages: string[] = [];
    const creditors = creditorsRef.current?.accept();
    if (creditors) messages.push(`Кредиторы: ${creditors}`);
    const property = propertyRef.current?.accept();
    if (property) messages.push(`Имущество: ${property}`);
    const incomePatch = mergeIncomePatches(suggestions);
    if (Object.keys(incomePatch).length) {
      update((p) => ({ ...p, income: { ...p.income, ...incomePatch } }));
      messages.push(`Доходы: ${suggestions.filter((s) => s.checked).map((s) => s.label).join('; ')}.`);
    }
    const appendix = appendixRef.current?.accept();
    if (appendix) messages.push(`Перечень приложений: ${appendix}`);
    setDone(messages.length ? messages : ['Отмеченного нет — ничего не внесено.']);
    setParsed(null);
  };

  const appendixLines = appendixItems.length ? buildAppendix({ items: appendixItems, profile, flags: computeBranching(profile) }) : [];
  const recognized = parsed ? parsed.appendix.results.length : 0;

  return (
    <div className="all-documents">
      <DocumentUpload
        label="Загрузить все документы клиента"
        busyLabel="Читаю документы…"
        hint="Кредитные отчёты, постановления ФССП, выписки ЕГРН и ГИБДД, счета, справки о доходах, ЕГРИП, ЕГИССО, справки о несудимости и другие. Приложение само разложит сведения по разделам и покажет их на проверку. Файлы нигде не сохраняются."
        busy={busy}
        disabled={readOnly}
        onFiles={(files) => void load(files)}
      />
      {progress && <p className="hint">{progress}</p>}

      {done.length > 0 && (
        <div className="note">
          {done.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}

      {parsed && (
        <div className="draft">
          <p>
            <strong>
              Распознано {recognized} из {parsed.total}.
            </strong>{' '}
            Проверьте отмеченное по разделам и внесите одной кнопкой внизу.
          </p>

          {(parsed.creditors.results.length > 0 || parsed.creditors.errors.length > 0) && (
            <section className="all-documents-section">
              <h3>Кредиторы</h3>
              <CreditorDocumentsImport
                ref={creditorsRef}
                caseId={profile.caseId}
                creditors={profile.creditors}
                debtorInn={profile.debtor.inn}
                readOnly={readOnly}
                preloaded={parsed.creditors}
                onAccept={(rows) => update((p) => withCreditors(p, rows))}
                onObligatory={(payments) => update((p) => withObligatory(p, payments))}
              />
            </section>
          )}

          {(parsed.property.results.length > 0 || parsed.property.errors.length > 0) && (
            <section className="all-documents-section">
              <h3>Имущество</h3>
              <PropertyDocumentsImport
                ref={propertyRef}
                caseId={profile.caseId}
                property={profile.property}
                debtorName={debtorName}
                debtorInn={profile.debtor.inn}
                readOnly={readOnly}
                preloaded={parsed.property}
                onAccept={(accepted) => update((p) => withProperty(p, accepted))}
              />
            </section>
          )}

          {suggestions.length > 0 && (
            <section className="all-documents-section">
              <h3>Доходы</h3>
              {GROUP_ORDER.filter((group) => suggestions.some((s) => s.group === group)).map((group) => (
                <div key={group} className="income-group">
                  <div className="field-label">{GROUP_TITLES[group]}</div>
                  {suggestions
                    .filter((s) => s.group === group)
                    .map((s) => (
                      <label key={s.id} className="income-choice">
                        <input type="checkbox" checked={s.checked} disabled={readOnly} onChange={() => toggle(s.id)} />
                        <span>
                          {s.label}
                          <span className="hint"> — {s.source}</span>
                        </span>
                      </label>
                    ))}
                </div>
              ))}
              <p className="hint">Все сведения документов о доходах с пояснениями — в разделе «Доходы», загрузкой там же.</p>
            </section>
          )}

          {parsed.appendix.results.length > 0 && (
            <section className="all-documents-section">
              <h3>Перечень приложений</h3>
              <AppendixDocumentsImport
                ref={appendixRef}
                caseId={profile.caseId}
                debtor={{ fullName: debtorName, birthDate: profile.debtor.birthDate, inn: profile.debtor.inn }}
                appendix={appendixLines.map((line) => ({ key: line.key, num: line.num, text: line.text }))}
                checklist={profile.legal.appendixChecklist}
                readOnly={readOnly}
                preloaded={parsed.appendix}
                onMark={(keys) =>
                  update((p) => ({
                    ...p,
                    legal: { ...p.legal, appendixChecklist: { ...p.legal.appendixChecklist, ...Object.fromEntries(keys.map((key) => [key, true])) } },
                  }))
                }
                onAddObligatory={(payment) => update((p) => ({ ...p, obligatoryPayments: [...p.obligatoryPayments, payment] }))}
              />
            </section>
          )}

          {parsed.failed.length > 0 && (
            <section className="all-documents-section">
              <h3>Не распознаны</h3>
              {parsed.failed.map((line) => (
                <p key={line} className="error-text">
                  {line}
                </p>
              ))}
            </section>
          )}

          <div className="toolbar">
            <button type="button" className="primary" disabled={readOnly} onClick={acceptAll}>
              Внести отмеченное
            </button>
            <button type="button" className="link-button" onClick={() => setParsed(null)}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
